import { animated, useSpring, useSpringRef, useTransition } from '@react-spring/web';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useOutsideClick } from '../../hooks/useOutsideClick';
import { Box } from '../../ui/Box';
import { theme } from '../../ui/theme';

import { SidebarMenuHeader } from './SidebarMenuHeader';
import { SidebarMenuItems } from './SidebarMenuItems';

const SidebarMenuContainer = styled(animated.div)`
    border-radius: ${theme.radii[8]};
    overflow: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
`;

const ToggleButton = styled(animated.button)<{ $isOpen: boolean }>`
    width: 100%;
    border: none;
    cursor: pointer;
    border-radius: ${(props) => (props.$isOpen ? '8px 8px 0 0' : '8px')};
    transition:
        transform 0.1s ease,
        background-color 150ms ease;
    background: transparent;

    &:hover,
    &:focus-visible {
        background-color: ${theme.colors.background.inset};
    }
`;

const MenuContent = styled(animated.div)`
    overflow: hidden;
`;

export const SidebarMenu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [toggleHeight, setToggleHeight] = useState(52);
    const menuRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);

    const transitionConfig = { tension: 600, friction: 50 };

    // Style constants
    const BACKGROUND_DEFAULT_WITH_NO_ALPHA = 'rgba(255, 255, 255, 0)';
    const BACKGROUND_DEFAULT_IN_RGBA = 'rgba(255, 255, 255, 1)';
    const SHADOW_LARGE_WITH_NO_ALPHA = '0px 8px 24px -2px rgba(25, 54, 67,0)';
    const SHADOW_LARGE = theme.shadows.large;
    const TRANSFORM_UP = 'translateY(-10px)';
    const TRANSFORM_NORMAL = 'translateY(0px)';
    const MAX_HEIGHT_CLOSED = '0px';
    const MAX_HEIGHT_OPEN = '220px';
    const BORDER_SUBTLE = `1px solid ${theme.colors.border.subtle}`;
    const BORDER_SUBTLE_WITH_NO_WIDTH = `0px solid ${theme.colors.border.subtle}`;

    const containerApi = useSpringRef();
    const containerSpring = useSpring({
        ref: containerApi,
        backgroundColor: BACKGROUND_DEFAULT_WITH_NO_ALPHA,
        boxShadow: SHADOW_LARGE_WITH_NO_ALPHA,
        config: transitionConfig,
    });

    // Measure the toggle button height once on mount
    useEffect(() => {
        if (toggleRef.current) {
            setToggleHeight(toggleRef.current.offsetHeight);
        }
    }, []);

    const closeMenu = () => {
        setIsOpen(false);
        containerApi.start({
            backgroundColor: BACKGROUND_DEFAULT_WITH_NO_ALPHA,
            boxShadow: SHADOW_LARGE_WITH_NO_ALPHA,
        });
    };

    useOutsideClick([menuRef, toggleRef], closeMenu);

    const menuTransition = useTransition(isOpen, {
        from: {
            opacity: 0,
            transform: TRANSFORM_UP,
            maxHeight: MAX_HEIGHT_CLOSED,
            borderTop: BORDER_SUBTLE_WITH_NO_WIDTH,
        },
        enter: {
            opacity: 1,
            transform: TRANSFORM_NORMAL,
            maxHeight: MAX_HEIGHT_OPEN,
            borderTop: BORDER_SUBTLE,
        },
        leave: {
            opacity: 0,
            transform: TRANSFORM_UP,
            maxHeight: MAX_HEIGHT_CLOSED,
            borderTop: BORDER_SUBTLE_WITH_NO_WIDTH,
        },
        config: transitionConfig,
    });

    const handleMouseDown = () => {
        containerApi.start({
            backgroundColor: BACKGROUND_DEFAULT_IN_RGBA,
            boxShadow: SHADOW_LARGE,
        });
    };

    const handleMouseUp = () => {
        containerApi.start({
            backgroundColor: isOpen ? BACKGROUND_DEFAULT_IN_RGBA : BACKGROUND_DEFAULT_WITH_NO_ALPHA,
            boxShadow: isOpen ? SHADOW_LARGE : SHADOW_LARGE_WITH_NO_ALPHA,
        });
    };

    return (
        <Box position="relative" height={`${toggleHeight}px`}>
            <SidebarMenuContainer ref={menuRef} style={containerSpring}>
                <ToggleButton
                    $isOpen={isOpen}
                    ref={toggleRef}
                    onClick={() => {
                        setIsOpen((prev) => {
                            const newIsOpen = !prev;
                            containerApi.start({
                                backgroundColor: newIsOpen
                                    ? BACKGROUND_DEFAULT_IN_RGBA
                                    : BACKGROUND_DEFAULT_WITH_NO_ALPHA,
                                boxShadow: newIsOpen ? SHADOW_LARGE : SHADOW_LARGE_WITH_NO_ALPHA,
                            });
                            return newIsOpen;
                        });
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <SidebarMenuHeader />
                </ToggleButton>

                {menuTransition((style, item) =>
                    item ? (
                        <MenuContent style={style}>
                            <SidebarMenuItems closeMenu={closeMenu} />
                        </MenuContent>
                    ) : null
                )}
            </SidebarMenuContainer>
        </Box>
    );
};
